'use client';

import { Shield, Terminal, Settings, Copy, Check, Zap, CheckCircle } from 'lucide-react';
import { BRAND } from "@/lib/brand";
import Link from 'next/link';
import { useState } from 'react';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-400" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-gray-400" />
      )}
    </button>
  );
}

const mcpConfig = `{
  "mcpServers": {
    "commons": {
      "url": "https://trycommons.com/api/mcp/http",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`;

export default function MCPIntegrationPage() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-50 rounded-2xl mb-6 shadow-sm">
          <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#6366f1" opacity="0.2"/>
            <path d="M2 17l10 5 10-5" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12l10 5 10-5" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 7l10 5 10-5-10-5-10 5z" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">MCP Integration</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {BRAND.name} exposes a full{' '}
          <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-900">
            Model Context Protocol
          </a>{' '}
          server. Connect any MCP-compatible AI tool — Claude, Cursor, Windsurf, Copilot, or your own.
        </p>
        <div className="flex items-center justify-center gap-2 mt-4">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
            <Terminal className="h-3 w-3 mr-1" />
            Streamable HTTP
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            26 tools available
          </span>
        </div>
      </div>

      {/* What You Get */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Zap className="h-5 w-5 text-gray-700" />
          What your AI tool can do
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { title: 'Read teams, projects & updates', desc: 'Query org structure, project statuses, and recent activity.' },
            { title: 'Browse standups & retros', desc: 'Get submissions, insights, action items, and team health.' },
            { title: 'Post updates & comments', desc: 'Create updates and reply to threads without leaving your tool.' },
            { title: 'Generate reports', desc: 'Built-in prompt templates for weekly rollups, project briefings, and health checks.' },
          ].map(({ title, desc }) => (
            <div key={title} className="flex gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
                <p className="text-sm text-gray-600">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Setup */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-700" />
          Setup
        </h2>
        <ol className="space-y-6">
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Create an API key</h3>
              <p className="text-sm text-gray-600">
                Go to your organization settings → <strong>API Keys</strong> and create a key with the <strong>&quot;MCP Server&quot;</strong> permission preset.
              </p>
            </div>
          </li>
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-2">Add the server to your MCP config</h3>
              <p className="text-sm text-gray-600 mb-3">
                Most MCP clients use a JSON config file. Add {BRAND.name} as a server:
              </p>
              <div className="relative">
                <pre className="text-xs bg-gray-900 text-gray-300 rounded-lg p-4 overflow-x-auto">
                  <code>{mcpConfig}</code>
                </pre>
                <CopyButton text={mcpConfig} />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Endpoint: <code className="px-1 py-0.5 bg-gray-100 rounded font-mono">https://trycommons.com/api/mcp/http</code>
              </p>
            </div>
          </li>
          <li className="flex gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-900 rounded-full flex items-center justify-center font-semibold text-sm">
              3
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Start using it</h3>
              <p className="text-sm text-gray-600">
                Your AI tool will discover {BRAND.name}'s tools, resources, and prompt templates automatically.
              </p>
            </div>
          </li>
        </ol>
      </div>

      {/* Compatible Tools */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Compatible tools</h2>
        <p className="text-sm text-gray-600 mb-6">
          Any tool that supports the MCP protocol works with {BRAND.name}. Some popular ones:
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { name: 'Claude Desktop', desc: 'Anthropic\'s desktop app' },
            { name: 'Claude Code', desc: 'CLI for software development' },
            { name: 'Cursor', desc: 'AI-powered code editor' },
            { name: 'Windsurf', desc: 'Codeium\'s AI IDE' },
            { name: 'VS Code + Copilot', desc: 'GitHub Copilot with MCP support' },
            { name: 'Your own client', desc: 'Any MCP SDK implementation' },
          ].map(({ name, desc }) => (
            <div key={name} className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-100">
              <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-gray-900">{name}</span>
                <span className="text-xs text-gray-500 ml-2">{desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <Shield className="h-5 w-5 text-gray-700" />
          Security
        </h2>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>API key auth</strong> — All requests require a valid key. No OAuth flow needed.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>Scoped permissions</strong> — Each key only accesses what you allow.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>Revocable anytime</strong> — Rotate or delete keys from org settings.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 mt-1">&#8226;</span>
            <span><strong>HTTPS only</strong> — All traffic is TLS-encrypted.</span>
          </li>
        </ul>
        <p className="text-sm text-gray-500 mt-4">
          For Claude-specific setup (Desktop and Code), see the <Link href="/integrations/claude" className="text-gray-900 underline hover:text-gray-700">Claude integration page</Link>.
        </p>
      </div>
    </div>
  );
}
